import {constructUsing, createMap, forMember, mapFrom, type Mapper} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {AgentRefereeRevenueReport} from '@igp/shared/contract/pam';
import {CommissionSubTypesHistorical} from '@igp/shared/contract/pam';
import {formatTimestamp, type IsNever} from '@igp/shared/utils';

import {AgentLevelReportV2DownloadViewModelsKeysTypeName} from './constants';
import type {
    AgentLevelReportV2ColumnSettingKeys,
    AgentLevelReportV2DownloadFields,
    AgentLevelReportV2DownloadModel,
    AgentLevelReportV2QueryKeys,
    AgentLevelReportV2SortKeys,
    AgentLevelReportV2ViewModel,
    AgentLevelReportV2ViewModelsKeys,
    CommissionAllSubTypesHistoricalViewModel,
    DataGridAgentLevelReportV2Keys,
} from './types';

export const agentLevelReportV2MappingKeys = {
    Model: 'AgentLevelReportV2.Model',
    ViewModel: 'AgentLevelReportV2.ViewModel',
    DownloadModel: 'AgentLevelReportV2.DownloadModel',
    ViewModelsKeys: 'AgentLevelReportV2.ViewModelsKeys',
    ColumnKeys: 'AgentLevelReportV2.ColumnKeys',
    SettingKeys: 'AgentLevelReportV2.SettingKeys',
    SortKeys: 'AgentLevelReportV2.SortKeys',
    QueryKeys: 'AgentLevelReportV2.QueryKeys',
    DownloadFields: 'AgentLevelReportV2.DownloadFields',
} as const;

export class AgentLevelReportV2Mapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<AgentRefereeRevenueReport, AgentLevelReportV2ViewModel>(
            mapper,
            agentLevelReportV2MappingKeys.Model,
            agentLevelReportV2MappingKeys.ViewModel,
            forMember(
                vm => vm.agentUid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.agentUsername,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.casinoCommissionableAmount,
                mapFrom(m => m.casino_commissionable_amount)
            ),
            forMember(
                vm => vm.casinoCosts,
                mapFrom(m => m.casino_costs)
            ),
            forMember(
                vm => vm.casinoGgr,
                mapFrom(m => m.casino_ggr)
            ),
            forMember(
                vm => vm.casinoNetShare,
                mapFrom(m => m.casino_net_share)
            ),
            forMember(
                vm => vm.casinoNgr,
                mapFrom(m => m.casino_ngr)
            ),
            forMember(
                vm => vm.casinoShareRatioPerc,
                mapFrom(m => m.casino_share_ratio_perc)
            ),
            forMember(
                vm => vm.globalSpinsCommissionableAmount,
                mapFrom(m => m.global_spins_commissionable_amount)
            ),
            forMember(
                vm => vm.globalSpinsCosts,
                mapFrom(m => m.global_spins_costs)
            ),
            forMember(
                vm => vm.globalSpinsGgr,
                mapFrom(m => m.global_spins_ggr)
            ),
            forMember(
                vm => vm.globalSpinsNetShare,
                mapFrom(m => m.global_spins_net_share)
            ),
            forMember(
                vm => vm.globalSpinsNgr,
                mapFrom(m => m.global_spins_ngr)
            ),
            forMember(
                vm => vm.globalSpinsShareRatioPerc,
                mapFrom(m => m.global_spins_share_ratio_perc)
            ),
            forMember(
                vm => vm.pokerCashPloCommissionableAmount,
                mapFrom(m => m.poker_cash_plo_commissionable_amount)
            ),
            forMember(
                vm => vm.pokerCashPloCosts,
                mapFrom(m => m.poker_cash_plo_costs)
            ),
            forMember(
                vm => vm.pokerCashPloGgr,
                mapFrom(m => m.poker_cash_plo_ggr)
            ),
            forMember(
                vm => vm.pokerCashPloNetShare,
                mapFrom(m => m.poker_cash_plo_net_share)
            ),
            forMember(
                vm => vm.pokerCashPloNgr,
                mapFrom(m => m.poker_cash_plo_ngr)
            ),
            forMember(
                vm => vm.pokerCashPloShareRatioPerc,
                mapFrom(m => m.poker_cash_plo_share_ratio_perc)
            ),
            forMember(
                vm => vm.pokerMttCommissionableAmount,
                mapFrom(m => m.poker_mtt_commissionable_amount)
            ),
            forMember(
                vm => vm.pokerMttCosts,
                mapFrom(m => m.poker_mtt_costs)
            ),
            forMember(
                vm => vm.pokerMttGgr,
                mapFrom(m => m.poker_mtt_ggr)
            ),
            forMember(
                vm => vm.pokerMttNetShare,
                mapFrom(m => m.poker_mtt_net_share)
            ),
            forMember(
                vm => vm.pokerMttNgr,
                mapFrom(m => m.poker_mtt_ngr)
            ),
            forMember(
                vm => vm.pokerMttShareRatioPerc,
                mapFrom(m => m.poker_mtt_share_ratio_perc)
            ),
            forMember(
                vm => vm.pokerNlheSdCommissionableAmount,
                mapFrom(m => m.poker_nlhe_sd_commissionable_amount)
            ),
            forMember(
                vm => vm.pokerNlheSdCosts,
                mapFrom(m => m.poker_nlhe_sd_costs)
            ),
            forMember(
                vm => vm.pokerNlheSdGgr,
                mapFrom(m => m.poker_nlhe_sd_ggr)
            ),
            forMember(
                vm => vm.pokerNlheSdNetShare,
                mapFrom(m => m.poker_nlhe_sd_net_share)
            ),
            forMember(
                vm => vm.pokerNlheSdNgr,
                mapFrom(m => m.poker_nlhe_sd_ngr)
            ),
            forMember(
                vm => vm.pokerNlheSdShareRatioPerc,
                mapFrom(m => m.poker_nlhe_sd_share_ratio_perc)
            ),
            forMember(
                vm => vm.rowId,
                mapFrom(m => m.row_id)
            ),
            forMember(
                vm => vm.sportsCommissionableAmount,
                mapFrom(m => m.sports_commissionable_amount)
            ),
            forMember(
                vm => vm.sportsCosts,
                mapFrom(m => m.sports_costs)
            ),
            forMember(
                vm => vm.sportsGgr,
                mapFrom(m => m.sports_ggr)
            ),
            forMember(
                vm => vm.sportsNetShare,
                mapFrom(m => m.sports_net_share)
            ),
            forMember(
                vm => vm.sportsNgr,
                mapFrom(m => m.sports_ngr)
            ),
            forMember(
                vm => vm.sportsShareRatioPerc,
                mapFrom(m => m.sports_share_ratio_perc)
            ),
            forMember(
                vm => vm.totalNetShare,
                mapFrom(m => m.total_net_share)
            ),
            forMember(
                vm => vm.weekStartDateTs,
                mapFrom(m => m.week_start_date_ts)
            ),
            forMember(
                vm => vm.commissionSubtypesPoker,
                mapFrom(m => this.getCommissionSubTypesHistoricalViewModel(m.commission_subtypes?.poker))
            ),
            forMember(
                vm => vm.commissionSubtypesPokerMtt,
                mapFrom(m => this.getCommissionSubTypesHistoricalViewModel(m.commission_subtypes?.poker_mtt))
            ),
            forMember(
                vm => vm.commissionSubtypesPokerCashPlo,
                mapFrom(m => this.getCommissionSubTypesHistoricalViewModel(m.commission_subtypes?.poker_cash_plo))
            ),
            forMember(
                vm => vm.commissionSubtypesCasino,
                mapFrom(m => this.getCommissionSubTypesHistoricalViewModel(m.commission_subtypes?.casino))
            ),
            forMember(
                vm => vm.commissionSubtypesSports,
                mapFrom(m => this.getCommissionSubTypesHistoricalViewModel(m.commission_subtypes?.sports))
            ),
            forMember(
                vm => vm.commissionSubtypesSpins,
                mapFrom(m => this.getCommissionSubTypesHistoricalViewModel(m.commission_subtypes?.spins))
            )
        );
        createMap<AgentRefereeRevenueReport, AgentLevelReportV2DownloadModel>(
            mapper,
            agentLevelReportV2MappingKeys.Model,
            agentLevelReportV2MappingKeys.DownloadModel,
            forMember(
                vm => vm.agentUid,
                mapFrom(m => m.agent_uid)
            ),
            forMember(
                vm => vm.agentUsername,
                mapFrom(m => m.agent_username)
            ),
            forMember(
                vm => vm.casinoCommissionableAmount,
                mapFrom(m => m.casino_commissionable_amount?.toString())
            ),
            forMember(
                vm => vm.casinoCosts,
                mapFrom(m => m.casino_costs?.toString())
            ),
            forMember(
                vm => vm.casinoGgr,
                mapFrom(m => m.casino_ggr?.toString())
            ),
            forMember(
                vm => vm.casinoNetShare,
                mapFrom(m => m.casino_net_share?.toString())
            ),
            forMember(
                vm => vm.casinoNgr,
                mapFrom(m => m.casino_ngr?.toString())
            ),
            forMember(
                vm => vm.casinoShareRatioPerc,
                mapFrom(m => m.casino_share_ratio_perc?.toString())
            ),
            forMember(
                vm => vm.globalSpinsCommissionableAmount,
                mapFrom(m => m.global_spins_commissionable_amount?.toString())
            ),
            forMember(
                vm => vm.globalSpinsCosts,
                mapFrom(m => m.global_spins_costs?.toString())
            ),
            forMember(
                vm => vm.globalSpinsGgr,
                mapFrom(m => m.global_spins_ggr?.toString())
            ),
            forMember(
                vm => vm.globalSpinsNetShare,
                mapFrom(m => m.global_spins_net_share?.toString())
            ),
            forMember(
                vm => vm.globalSpinsNgr,
                mapFrom(m => m.global_spins_ngr?.toString())
            ),
            forMember(
                vm => vm.globalSpinsShareRatioPerc,
                mapFrom(m => m.global_spins_share_ratio_perc?.toString())
            ),
            forMember(
                vm => vm.pokerCashPloCommissionableAmount,
                mapFrom(m => m.poker_cash_plo_commissionable_amount?.toString())
            ),
            forMember(
                vm => vm.pokerCashPloCosts,
                mapFrom(m => m.poker_cash_plo_costs?.toString())
            ),
            forMember(
                vm => vm.pokerCashPloGgr,
                mapFrom(m => m.poker_cash_plo_ggr?.toString())
            ),
            forMember(
                vm => vm.pokerCashPloNetShare,
                mapFrom(m => m.poker_cash_plo_net_share?.toString())
            ),
            forMember(
                vm => vm.pokerCashPloNgr,
                mapFrom(m => m.poker_cash_plo_ngr?.toString())
            ),
            forMember(
                vm => vm.pokerCashPloShareRatioPerc,
                mapFrom(m => m.poker_cash_plo_share_ratio_perc?.toString())
            ),
            forMember(
                vm => vm.pokerMttCommissionableAmount,
                mapFrom(m => m.poker_mtt_commissionable_amount?.toString())
            ),
            forMember(
                vm => vm.pokerMttCosts,
                mapFrom(m => m.poker_mtt_costs?.toString())
            ),
            forMember(
                vm => vm.pokerMttGgr,
                mapFrom(m => m.poker_mtt_ggr?.toString())
            ),
            forMember(
                vm => vm.pokerMttNetShare,
                mapFrom(m => m.poker_mtt_net_share?.toString())
            ),
            forMember(
                vm => vm.pokerMttNgr,
                mapFrom(m => m.poker_mtt_ngr?.toString())
            ),
            forMember(
                vm => vm.pokerMttShareRatioPerc,
                mapFrom(m => m.poker_mtt_share_ratio_perc?.toString())
            ),
            forMember(
                vm => vm.pokerNlheSdCommissionableAmount,
                mapFrom(m => m.poker_nlhe_sd_commissionable_amount?.toString())
            ),
            forMember(
                vm => vm.pokerNlheSdCosts,
                mapFrom(m => m.poker_nlhe_sd_costs?.toString())
            ),
            forMember(
                vm => vm.pokerNlheSdGgr,
                mapFrom(m => m.poker_nlhe_sd_ggr?.toString())
            ),
            forMember(
                vm => vm.pokerNlheSdNetShare,
                mapFrom(m => m.poker_nlhe_sd_net_share?.toString())
            ),
            forMember(
                vm => vm.pokerNlheSdNgr,
                mapFrom(m => m.poker_nlhe_sd_ngr?.toString())
            ),
            forMember(
                vm => vm.pokerNlheSdShareRatioPerc,
                mapFrom(m => m.poker_nlhe_sd_share_ratio_perc?.toString())
            ),
            forMember(
                vm => vm.rowId,
                mapFrom(m => m.row_id)
            ),
            forMember(
                vm => vm.sportsCommissionableAmount,
                mapFrom(m => m.sports_commissionable_amount?.toString())
            ),
            forMember(
                vm => vm.sportsCosts,
                mapFrom(m => m.sports_costs?.toString())
            ),
            forMember(
                vm => vm.sportsGgr,
                mapFrom(m => m.sports_ggr?.toString())
            ),
            forMember(
                vm => vm.sportsNetShare,
                mapFrom(m => m.sports_net_share?.toString())
            ),
            forMember(
                vm => vm.sportsNgr,
                mapFrom(m => m.sports_ngr?.toString())
            ),
            forMember(
                vm => vm.sportsShareRatioPerc,
                mapFrom(m => m.sports_share_ratio_perc?.toString())
            ),
            forMember(
                vm => vm.totalNetShare,
                mapFrom(m => m.total_net_share?.toString())
            ),
            forMember(
                vm => vm.weekStartDateTs,
                mapFrom(m => formatTimestamp(m.week_start_date_ts, 'date'))
            )
        );
        createMap<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2QueryKeys>(
            mapper,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            agentLevelReportV2MappingKeys.QueryKeys,
            constructUsing(m => {
                const keysMapper: Record<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2QueryKeys> = {
                    agentUid: 'items.agent_uid',
                    agentUsername: 'items.agent_username',
                    casinoCommissionableAmount: 'items.casino_commissionable_amount',
                    casinoCosts: 'items.casino_costs',
                    casinoGgr: 'items.casino_ggr',
                    casinoNetShare: 'items.casino_net_share',
                    casinoNgr: 'items.casino_ngr',
                    casinoShareRatioPerc: 'items.casino_share_ratio_perc',
                    globalSpinsCommissionableAmount: 'items.global_spins_commissionable_amount',
                    globalSpinsCosts: 'items.global_spins_costs',
                    globalSpinsGgr: 'items.global_spins_ggr',
                    globalSpinsNetShare: 'items.global_spins_net_share',
                    globalSpinsNgr: 'items.global_spins_ngr',
                    globalSpinsShareRatioPerc: 'items.global_spins_share_ratio_perc',
                    pokerCashPloCommissionableAmount: 'items.poker_cash_plo_commissionable_amount',
                    pokerCashPloCosts: 'items.poker_cash_plo_costs',
                    pokerCashPloGgr: 'items.poker_cash_plo_ggr',
                    pokerCashPloNetShare: 'items.poker_cash_plo_net_share',
                    pokerCashPloNgr: 'items.poker_cash_plo_ngr',
                    pokerCashPloShareRatioPerc: 'items.poker_cash_plo_share_ratio_perc',
                    pokerMttCommissionableAmount: 'items.poker_mtt_commissionable_amount',
                    pokerMttCosts: 'items.poker_mtt_costs',
                    pokerMttGgr: 'items.poker_mtt_ggr',
                    pokerMttNetShare: 'items.poker_mtt_net_share',
                    pokerMttNgr: 'items.poker_mtt_ngr',
                    pokerMttShareRatioPerc: 'items.poker_mtt_share_ratio_perc',
                    pokerNlheSdCommissionableAmount: 'items.poker_nlhe_sd_commissionable_amount',
                    pokerNlheSdCosts: 'items.poker_nlhe_sd_costs',
                    pokerNlheSdGgr: 'items.poker_nlhe_sd_ggr',
                    pokerNlheSdNetShare: 'items.poker_nlhe_sd_net_share',
                    pokerNlheSdNgr: 'items.poker_nlhe_sd_ngr',
                    pokerNlheSdShareRatioPerc: 'items.poker_nlhe_sd_share_ratio_perc',
                    rowId: 'items.row_id',
                    sportsCommissionableAmount: 'items.sports_commissionable_amount',
                    sportsCosts: 'items.sports_costs',
                    sportsGgr: 'items.sports_ggr',
                    sportsNetShare: 'items.sports_net_share',
                    sportsNgr: 'items.sports_ngr',
                    sportsShareRatioPerc: 'items.sports_share_ratio_perc',
                    totalNetShare: 'items.total_net_share',
                    weekStartDateTs: 'items.week_start_date_ts.seconds',
                    commissionSubtypesPoker: 'items.commission_subtypes.poker',
                    commissionSubtypesPokerMtt: 'items.commission_subtypes.poker_mtt',
                    commissionSubtypesPokerCashPlo: 'items.commission_subtypes.poker_cash_plo',
                    commissionSubtypesCasino: 'items.commission_subtypes.casino',
                    commissionSubtypesSports: 'items.commission_subtypes.sports',
                    commissionSubtypesSpins: 'items.commission_subtypes.spins',
                };

                return keysMapper[m];
            })
        );
        createMap<DataGridAgentLevelReportV2Keys[], AgentLevelReportV2ViewModelsKeys[]>(
            mapper,
            agentLevelReportV2MappingKeys.ColumnKeys,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            constructUsing(m => {
                const keysMapper: Record<DataGridAgentLevelReportV2Keys, AgentLevelReportV2ViewModelsKeys[]> = {
                    agentUid: ['agentUid'],
                    agentUsername: ['agentUsername'],
                    casinoCommissionableAmount: ['casinoCommissionableAmount'],
                    casinoCosts: ['casinoCosts'],
                    casinoGgr: ['casinoGgr'],
                    casinoNetShare: ['casinoNetShare'],
                    casinoNgr: ['casinoNgr'],
                    casinoShareRatioPerc: ['casinoShareRatioPerc'],
                    globalSpinsCommissionableAmount: ['globalSpinsCommissionableAmount'],
                    globalSpinsCosts: ['globalSpinsCosts'],
                    globalSpinsGgr: ['globalSpinsGgr'],
                    globalSpinsNetShare: ['globalSpinsNetShare'],
                    globalSpinsNgr: ['globalSpinsNgr'],
                    globalSpinsShareRatioPerc: ['globalSpinsShareRatioPerc'],
                    pokerCashPloCommissionableAmount: ['pokerCashPloCommissionableAmount'],
                    pokerCashPloCosts: ['pokerCashPloCosts'],
                    pokerCashPloGgr: ['pokerCashPloGgr'],
                    pokerCashPloNetShare: ['pokerCashPloNetShare'],
                    pokerCashPloNgr: ['pokerCashPloNgr'],
                    pokerCashPloShareRatioPerc: ['pokerCashPloShareRatioPerc'],
                    pokerMttCommissionableAmount: ['pokerMttCommissionableAmount'],
                    pokerMttCosts: ['pokerMttCosts'],
                    pokerMttGgr: ['pokerMttGgr'],
                    pokerMttNetShare: ['pokerMttNetShare'],
                    pokerMttNgr: ['pokerMttNgr'],
                    pokerMttShareRatioPerc: ['pokerMttShareRatioPerc'],
                    pokerNlheSdCommissionableAmount: ['pokerNlheSdCommissionableAmount'],
                    pokerNlheSdCosts: ['pokerNlheSdCosts'],
                    pokerNlheSdGgr: ['pokerNlheSdGgr'],
                    pokerNlheSdNetShare: ['pokerNlheSdNetShare'],
                    pokerNlheSdNgr: ['pokerNlheSdNgr'],
                    pokerNlheSdShareRatioPerc: ['pokerNlheSdShareRatioPerc'],
                    rowId: ['rowId'],
                    sportsCommissionableAmount: ['sportsCommissionableAmount'],
                    sportsCosts: ['sportsCosts'],
                    sportsGgr: ['sportsGgr'],
                    sportsNetShare: ['sportsNetShare'],
                    sportsNgr: ['sportsNgr'],
                    sportsShareRatioPerc: ['sportsShareRatioPerc'],
                    totalNetShare: ['totalNetShare'],
                    weekStartDateTs: ['weekStartDateTs'],
                    details: ['rowId', 'agentUid'],
                };

                return [...new Set(m.flatMap(f => keysMapper[f] ?? []))];
            })
        );
        createMap<DataGridAgentLevelReportV2Keys[], AgentLevelReportV2ViewModelsKeys[]>(
            mapper,
            agentLevelReportV2MappingKeys.ColumnKeys,
            AgentLevelReportV2DownloadViewModelsKeysTypeName,
            constructUsing(m => {
                const keysMapper: Record<DataGridAgentLevelReportV2Keys, AgentLevelReportV2ViewModelsKeys[]> = {
                    agentUid: ['agentUid'],
                    agentUsername: ['agentUsername'],
                    casinoCommissionableAmount: ['casinoCommissionableAmount'],
                    casinoCosts: ['casinoCosts'],
                    casinoGgr: ['casinoGgr'],
                    casinoNetShare: ['casinoNetShare'],
                    casinoNgr: ['casinoNgr'],
                    casinoShareRatioPerc: ['casinoShareRatioPerc'],
                    globalSpinsCommissionableAmount: ['globalSpinsCommissionableAmount'],
                    globalSpinsCosts: ['globalSpinsCosts'],
                    globalSpinsGgr: ['globalSpinsGgr'],
                    globalSpinsNetShare: ['globalSpinsNetShare'],
                    globalSpinsNgr: ['globalSpinsNgr'],
                    globalSpinsShareRatioPerc: ['globalSpinsShareRatioPerc'],
                    pokerCashPloCommissionableAmount: ['pokerCashPloCommissionableAmount'],
                    pokerCashPloCosts: ['pokerCashPloCosts'],
                    pokerCashPloGgr: ['pokerCashPloGgr'],
                    pokerCashPloNetShare: ['pokerCashPloNetShare'],
                    pokerCashPloNgr: ['pokerCashPloNgr'],
                    pokerCashPloShareRatioPerc: ['pokerCashPloShareRatioPerc'],
                    pokerMttCommissionableAmount: ['pokerMttCommissionableAmount'],
                    pokerMttCosts: ['pokerMttCosts'],
                    pokerMttGgr: ['pokerMttGgr'],
                    pokerMttNetShare: ['pokerMttNetShare'],
                    pokerMttNgr: ['pokerMttNgr'],
                    pokerMttShareRatioPerc: ['pokerMttShareRatioPerc'],
                    pokerNlheSdCommissionableAmount: ['pokerNlheSdCommissionableAmount'],
                    pokerNlheSdCosts: ['pokerNlheSdCosts'],
                    pokerNlheSdGgr: ['pokerNlheSdGgr'],
                    pokerNlheSdNetShare: ['pokerNlheSdNetShare'],
                    pokerNlheSdNgr: ['pokerNlheSdNgr'],
                    pokerNlheSdShareRatioPerc: ['pokerNlheSdShareRatioPerc'],
                    rowId: ['rowId'],
                    sportsCommissionableAmount: ['sportsCommissionableAmount'],
                    sportsCosts: ['sportsCosts'],
                    sportsGgr: ['sportsGgr'],
                    sportsNetShare: ['sportsNetShare'],
                    sportsNgr: ['sportsNgr'],
                    sportsShareRatioPerc: ['sportsShareRatioPerc'],
                    totalNetShare: ['totalNetShare'],
                    weekStartDateTs: ['weekStartDateTs'],
                    details: [],
                };

                return [...new Set(m.flatMap(f => keysMapper[f] ?? []))];
            })
        );

        createMap<DataGridAgentLevelReportV2Keys, AgentLevelReportV2ColumnSettingKeys>(
            mapper,
            agentLevelReportV2MappingKeys.ColumnKeys,
            agentLevelReportV2MappingKeys.SettingKeys,
            constructUsing(m => {
                type FieldsToMap = Exclude<DataGridAgentLevelReportV2Keys, AgentLevelReportV2ColumnSettingKeys>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return m;
            })
        );
        createMap<AgentLevelReportV2ColumnSettingKeys, DataGridAgentLevelReportV2Keys>(
            mapper,
            agentLevelReportV2MappingKeys.SettingKeys,
            agentLevelReportV2MappingKeys.ColumnKeys,
            constructUsing(m => {
                type FieldsToMap = Exclude<AgentLevelReportV2ColumnSettingKeys, DataGridAgentLevelReportV2Keys | '__type'>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return m === '__type' ? undefined : m;
            })
        );

        createMap<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2SortKeys>(
            mapper,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            agentLevelReportV2MappingKeys.SortKeys,
            constructUsing(m => {
                const fieldsMapper: Record<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2SortKeys> = {
                    agentUid: 'agent_uid',
                    agentUsername: 'agent_username',
                    casinoCommissionableAmount: 'casino_commissionable_amount',
                    casinoCosts: 'casino_costs',
                    casinoGgr: 'casino_ggr',
                    casinoNetShare: 'casino_net_share',
                    casinoNgr: 'casino_ngr',
                    casinoShareRatioPerc: 'casino_share_ratio_perc',
                    globalSpinsCommissionableAmount: 'global_spins_commissionable_amount',
                    globalSpinsCosts: 'global_spins_costs',
                    globalSpinsGgr: 'global_spins_ggr',
                    globalSpinsNetShare: 'global_spins_net_share',
                    globalSpinsNgr: 'global_spins_ngr',
                    globalSpinsShareRatioPerc: 'global_spins_share_ratio_perc',
                    pokerCashPloCommissionableAmount: 'poker_cash_plo_commissionable_amount',
                    pokerCashPloCosts: 'poker_cash_plo_costs',
                    pokerCashPloGgr: 'poker_cash_plo_ggr',
                    pokerCashPloNetShare: 'poker_cash_plo_net_share',
                    pokerCashPloNgr: 'poker_cash_plo_ngr',
                    pokerCashPloShareRatioPerc: 'poker_cash_plo_share_ratio_perc',
                    pokerMttCommissionableAmount: 'poker_mtt_commissionable_amount',
                    pokerMttCosts: 'poker_mtt_costs',
                    pokerMttGgr: 'poker_mtt_ggr',
                    pokerMttNetShare: 'poker_mtt_net_share',
                    pokerMttNgr: 'poker_mtt_ngr',
                    pokerMttShareRatioPerc: 'poker_mtt_share_ratio_perc',
                    pokerNlheSdCommissionableAmount: 'poker_nlhe_sd_commissionable_amount',
                    pokerNlheSdCosts: 'poker_nlhe_sd_costs',
                    pokerNlheSdGgr: 'poker_nlhe_sd_ggr',
                    pokerNlheSdNetShare: 'poker_nlhe_sd_net_share',
                    pokerNlheSdNgr: 'poker_nlhe_sd_ngr',
                    pokerNlheSdShareRatioPerc: 'poker_nlhe_sd_share_ratio_perc',
                    rowId: 'row_id',
                    sportsCommissionableAmount: 'sports_commissionable_amount',
                    sportsCosts: 'sports_costs',
                    sportsGgr: 'sports_ggr',
                    sportsNetShare: 'sports_net_share',
                    sportsNgr: 'sports_ngr',
                    sportsShareRatioPerc: 'sports_share_ratio_perc',
                    totalNetShare: 'total_net_share',
                    weekStartDateTs: 'week_start_date_ts.seconds',
                    commissionSubtypesCasino: null,
                    commissionSubtypesPoker: null,
                    commissionSubtypesPokerCashPlo: null,
                    commissionSubtypesPokerMtt: null,
                    commissionSubtypesSpins: null,
                    commissionSubtypesSports: null,
                };
                return fieldsMapper[m];
            })
        );

        createMap<AgentLevelReportV2SortKeys, AgentLevelReportV2ViewModelsKeys>(
            mapper,
            agentLevelReportV2MappingKeys.SortKeys,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            constructUsing(m => {
                const fieldsMapper: Record<AgentLevelReportV2SortKeys, AgentLevelReportV2ViewModelsKeys> = {
                    agent_uid: 'agentUid',
                    agent_username: 'agentUsername',
                    casino_commissionable_amount: 'casinoCommissionableAmount',
                    casino_costs: 'casinoCosts',
                    casino_ggr: 'casinoGgr',
                    casino_net_share: 'casinoNetShare',
                    casino_ngr: 'casinoNgr',
                    casino_share_ratio_perc: 'casinoShareRatioPerc',
                    global_spins_commissionable_amount: 'globalSpinsCommissionableAmount',
                    global_spins_costs: 'globalSpinsCosts',
                    global_spins_ggr: 'globalSpinsGgr',
                    global_spins_net_share: 'globalSpinsNetShare',
                    global_spins_ngr: 'globalSpinsNgr',
                    global_spins_share_ratio_perc: 'globalSpinsShareRatioPerc',
                    poker_cash_plo_commissionable_amount: 'pokerCashPloCommissionableAmount',
                    poker_cash_plo_costs: 'pokerCashPloCosts',
                    poker_cash_plo_ggr: 'pokerCashPloGgr',
                    poker_cash_plo_net_share: 'pokerCashPloNetShare',
                    poker_cash_plo_ngr: 'pokerCashPloNgr',
                    poker_cash_plo_share_ratio_perc: 'pokerCashPloShareRatioPerc',
                    poker_mtt_commissionable_amount: 'pokerMttCommissionableAmount',
                    poker_mtt_costs: 'pokerMttCosts',
                    poker_mtt_ggr: 'pokerMttGgr',
                    poker_mtt_net_share: 'pokerMttNetShare',
                    poker_mtt_ngr: 'pokerMttNgr',
                    poker_mtt_share_ratio_perc: 'pokerMttShareRatioPerc',
                    poker_nlhe_sd_commissionable_amount: 'pokerNlheSdCommissionableAmount',
                    poker_nlhe_sd_costs: 'pokerNlheSdCosts',
                    poker_nlhe_sd_ggr: 'pokerNlheSdGgr',
                    poker_nlhe_sd_net_share: 'pokerNlheSdNetShare',
                    poker_nlhe_sd_ngr: 'pokerNlheSdNgr',
                    poker_nlhe_sd_share_ratio_perc: 'pokerNlheSdShareRatioPerc',
                    row_id: 'rowId',
                    sports_commissionable_amount: 'sportsCommissionableAmount',
                    sports_costs: 'sportsCosts',
                    sports_ggr: 'sportsGgr',
                    sports_net_share: 'sportsNetShare',
                    sports_ngr: 'sportsNgr',
                    sports_share_ratio_perc: 'sportsShareRatioPerc',
                    total_net_share: 'totalNetShare',
                    'week_start_date_ts.seconds': 'weekStartDateTs',
                };
                return fieldsMapper[m];
            })
        );
        createMap<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2DownloadFields>(
            mapper,
            agentLevelReportV2MappingKeys.ViewModelsKeys,
            agentLevelReportV2MappingKeys.DownloadFields,
            constructUsing(m => {
                const fieldsMapper: Record<AgentLevelReportV2ViewModelsKeys, AgentLevelReportV2DownloadFields> = {
                    agentUid: 'agent_uid',
                    agentUsername: 'agent_username',
                    casinoCommissionableAmount: 'casino_commissionable_amount',
                    casinoCosts: 'casino_costs',
                    casinoGgr: 'casino_ggr',
                    casinoNetShare: 'casino_net_share',
                    casinoNgr: 'casino_ngr',
                    casinoShareRatioPerc: 'casino_share_ratio_perc',
                    globalSpinsCommissionableAmount: 'global_spins_commissionable_amount',
                    globalSpinsCosts: 'global_spins_costs',
                    globalSpinsGgr: 'global_spins_ggr',
                    globalSpinsNetShare: 'global_spins_net_share',
                    globalSpinsNgr: 'global_spins_ngr',
                    globalSpinsShareRatioPerc: 'global_spins_share_ratio_perc',
                    pokerCashPloCommissionableAmount: 'poker_cash_plo_commissionable_amount',
                    pokerCashPloCosts: 'poker_cash_plo_costs',
                    pokerCashPloGgr: 'poker_cash_plo_ggr',
                    pokerCashPloNetShare: 'poker_cash_plo_net_share',
                    pokerCashPloNgr: 'poker_cash_plo_ngr',
                    pokerCashPloShareRatioPerc: 'poker_cash_plo_share_ratio_perc',
                    pokerMttCommissionableAmount: 'poker_mtt_commissionable_amount',
                    pokerMttCosts: 'poker_mtt_costs',
                    pokerMttGgr: 'poker_mtt_ggr',
                    pokerMttNetShare: 'poker_mtt_net_share',
                    pokerMttNgr: 'poker_mtt_ngr',
                    pokerMttShareRatioPerc: 'poker_mtt_share_ratio_perc',
                    pokerNlheSdCommissionableAmount: 'poker_nlhe_sd_commissionable_amount',
                    pokerNlheSdCosts: 'poker_nlhe_sd_costs',
                    pokerNlheSdGgr: 'poker_nlhe_sd_ggr',
                    pokerNlheSdNetShare: 'poker_nlhe_sd_net_share',
                    pokerNlheSdNgr: 'poker_nlhe_sd_ngr',
                    pokerNlheSdShareRatioPerc: 'poker_nlhe_sd_share_ratio_perc',
                    rowId: 'row_id',
                    sportsCommissionableAmount: 'sports_commissionable_amount',
                    sportsCosts: 'sports_costs',
                    sportsGgr: 'sports_ggr',
                    sportsNetShare: 'sports_net_share',
                    sportsNgr: 'sports_ngr',
                    sportsShareRatioPerc: 'sports_share_ratio_perc',
                    totalNetShare: 'total_net_share',
                    weekStartDateTs: 'week_start_date_ts.seconds',
                    commissionSubtypesPoker: 'commission_subtypes.poker',
                    commissionSubtypesPokerMtt: 'commission_subtypes.poker_mtt',
                    commissionSubtypesPokerCashPlo: 'commission_subtypes.poker_cash_plo',
                    commissionSubtypesCasino: 'commission_subtypes.casino',
                    commissionSubtypesSports: 'commission_subtypes.sports',
                    commissionSubtypesSpins: 'commission_subtypes.spins',
                };
                return fieldsMapper[m];
            })
        );
    }

    private getCommissionSubTypesHistoricalViewModel(
        commissionSubType: CommissionSubTypesHistorical
    ): CommissionAllSubTypesHistoricalViewModel {
        const commissionSubTypesMapping: Record<CommissionSubTypesHistorical, CommissionAllSubTypesHistoricalViewModel> = {
            [CommissionSubTypesHistorical.CasinoGgr]: 'CasinoGgr',
            [CommissionSubTypesHistorical.CasinoGgrAfterExternalCosts]: 'CasinoGgrAfterExternalCosts',
            [CommissionSubTypesHistorical.CasinoWager]: 'CasinoWager',
            [CommissionSubTypesHistorical.GlobalSpinsExpectedEggr]: 'GlobalSpinsGgr',
            [CommissionSubTypesHistorical.PokerCashPloGgr]: 'PloGgr',
            [CommissionSubTypesHistorical.PokerGgrAfterProPenaltiesAfterEcology]: 'PokerGgr',
            [CommissionSubTypesHistorical.PokerGgrAfterProPenaltiesAfterEcologyNoInsurance]: 'PokerGgrNoInsurance',
            [CommissionSubTypesHistorical.PokerMttGgr]: 'MttGgr',
            [CommissionSubTypesHistorical.PokerNgrExcPloAfterProPenaltiesAfterEcology]: 'PokerNgr',
            [CommissionSubTypesHistorical.SportsGgr]: 'SportsGgr',
            [CommissionSubTypesHistorical.SportsWagerForSettledBets]: 'SportsWager',
            [CommissionSubTypesHistorical.Mixed]: 'Mixed',
        };

        return commissionSubType ? commissionSubTypesMapping[commissionSubType] : undefined;
    }
}
